import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  ButtonDropdown,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Link,
  Popover,
  Select,
  SpaceBetween,
} from "@cloudscape-design/components";
import OperationsTrends from "./OperationsTrends";
import OperationsReportTable from "./OperationsReportTable";
import Board from "@cloudscape-design/board-components/board";
import BoardItem from "@cloudscape-design/board-components/board-item";
import { apiFetch } from "../../auth/apiClient";

const STORAGE_KEY = "operations-and-timeliness-layout-v13";
export const FISCAL_YEAR_OPTIONS = [
  { label: "2026–27", value: "2026-27", startYear: 2026 },
  { label: "2025–26", value: "2025-26", startYear: 2025 },
  { label: "2024–25", value: "2024-25", startYear: 2024 },
];

const STAFF_OPTIONS = [
  { label: "All staff", value: "all" },
];

const REGION_OPTIONS = [
  { label: "All regions", value: "all" },
  { label: "Unassigned pot", value: "UNASSIGNED" },
  { label: "NWAC National", value: "CA" },
  { label: "AB", value: "AB" },
  { label: "BC", value: "BC" },
  { label: "MB", value: "MB" },
  { label: "NB", value: "NB" },
  { label: "NL", value: "NL" },
  { label: "NT", value: "NT" },
  { label: "NS", value: "NS" },
  { label: "NU", value: "NU" },
  { label: "ON", value: "ON" },
  { label: "PE", value: "PE" },
  { label: "QC", value: "QC" },
  { label: "SK", value: "SK" },
  { label: "YT", value: "YT" },
];

export const MONTHS = [
  { number: "04", label: "Apr", quarter: "q1" },
  { number: "05", label: "May", quarter: "q1" },
  { number: "06", label: "Jun", quarter: "q1" },
  { number: "07", label: "Jul", quarter: "q2" },
  { number: "08", label: "Aug", quarter: "q2" },
  { number: "09", label: "Sep", quarter: "q2" },
  { number: "10", label: "Oct", quarter: "q3" },
  { number: "11", label: "Nov", quarter: "q3" },
  { number: "12", label: "Dec", quarter: "q3" },
  { number: "01", label: "Jan", quarter: "q4" },
  { number: "02", label: "Feb", quarter: "q4" },
  { number: "03", label: "Mar", quarter: "q4" },
];

export const PERIOD_OPTIONS = [
  { label: "Full year", value: "full", monthIndexes: MONTHS.map((_, index) => index) },
  { label: "Q1 (Apr–Jun)", value: "q1", monthIndexes: [0, 1, 2] },
  { label: "April", value: "m0", monthIndexes: [0] },
  { label: "May", value: "m1", monthIndexes: [1] },
  { label: "June", value: "m2", monthIndexes: [2] },
  { label: "Q2 (Jul–Sep)", value: "q2", monthIndexes: [3, 4, 5] },
  { label: "July", value: "m3", monthIndexes: [3] },
  { label: "August", value: "m4", monthIndexes: [4] },
  { label: "September", value: "m5", monthIndexes: [5] },
  { label: "Q3 (Oct–Dec)", value: "q3", monthIndexes: [6, 7, 8] },
  { label: "October", value: "m6", monthIndexes: [6] },
  { label: "November", value: "m7", monthIndexes: [7] },
  { label: "December", value: "m8", monthIndexes: [8] },
  { label: "Q4 (Jan–Mar)", value: "q4", monthIndexes: [9, 10, 11] },
  { label: "January", value: "m9", monthIndexes: [9] },
  { label: "February", value: "m10", monthIndexes: [10] },
  { label: "March", value: "m11", monthIndexes: [11] },
];

const sum = (rows, key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);

const BACKLOG_STEP_ORDER = [
  "Awaiting assignment",
  "Submitted - not yet in assessment",
  "Awaiting coordinator assessment",
  "Awaiting applicant documents",
  "Awaiting coordinator changes",
  "Awaiting Regional Manager action",
  "Awaiting program decision",
  "On hold",
  "Other open work",
];

const sumFunding = rows => {
  const eiCents = rows.reduce((total, row) => total + Math.round(row.eiFunding * 100), 0);
  const crfCents = rows.reduce((total, row) => total + Math.round(row.crfFunding * 100), 0);
  return { eiFunding: eiCents / 100, crfFunding: crfCents / 100, totalFunding: (eiCents + crfCents) / 100 };
};
const fundingForStaff = (snapshot, staffId, region) => sumFunding(
  (snapshot.fundingActivity || []).filter(row =>
    String(row.staffId) === String(staffId) && (region === "all" || row.province === region)),
);

export const buildRegionalFundingRows = (snapshot, { staff = "all", region = "all" } = {}) => {
  // Funding is already resolved for the selected period by the shared report reader.
  const rows = (snapshot.fundingActivity || []).filter(row =>
    (staff === "all" || String(row.staffId) === String(staff)) &&
    (region === "all" || row.province === region));
  const regions = new Map();
  rows.forEach(row => {
    const province = row.province || "Unspecified";
    if (!regions.has(province)) regions.set(province, []);
    regions.get(province).push(row);
  });
  const items = [...regions.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([province, funding]) => ({ id: `region:${province}`, region: province === "UNASSIGNED" ? "Unassigned pot" : province === "CA" ? "NWAC National" : province, ...sumFunding(funding) }));
  if (items.length) items.push({ id: "overall-total", region: "Overall total", isTotal: true, ...sumFunding(rows) });
  return items;
};

const formatCurrency = value => `C$${Number(value).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FUNDING_COLUMNS = [
  { id: "eiFunding", header: "EI", cell: row => formatCurrency(row.eiFunding) },
  { id: "crfFunding", header: "CRF", cell: row => formatCurrency(row.crfFunding) },
  { id: "totalFunding", header: "Total Funding", cell: row => formatCurrency(row.totalFunding) },
];

export const buildSnapshotApplicationWorkloadRows = (
  snapshot,
  { fiscalYear, period = "full", staff = "all", region = "all" },
) => {
  const fiscalYearOption =
    FISCAL_YEAR_OPTIONS.find(option => option.value === fiscalYear) || FISCAL_YEAR_OPTIONS[0];
  const periodOption = PERIOD_OPTIONS.find(option => option.value === period) || PERIOD_OPTIONS[0];
  const includedMonths = new Set(
    periodOption.monthIndexes.map(monthIndex => {
      const month = MONTHS[monthIndex];
      const calendarYear =
        monthIndex < 9 ? fiscalYearOption.startYear : fiscalYearOption.startYear + 1;
      return `${calendarYear}-${month.number}`;
    }),
  );

  return (snapshot.staffMembers || [])
    .filter(manager => staff === "all" || String(manager.value) === String(staff))
    .filter(manager => region === "all" || (manager.provinces || []).includes(region))
    .map(manager => {
      const managerActivity = (snapshot.workloadActivity || []).filter(
        row =>
          String(row.staffId) === String(manager.value) &&
          includedMonths.has(row.month) &&
          (region === "all" || row.province === region),
      );
      const managerCurrentWork = (snapshot.currentWorkload || []).filter(
        row =>
          String(row.staffId) === String(manager.value) &&
          (region === "all" || row.province === region),
      );
      return {
        id: String(manager.value),
        staff: manager.label,
        region: (manager.provinces || []).join(", ") || "—",
        ...fundingForStaff(snapshot, manager.value, region),
        assigned: sum(managerActivity, "assigned"),
        notStarted: sum(managerActivity, "notStarted"),
        inProgress: sum(managerActivity, "inProgress"),
        assessed: sum(managerActivity, "assessed"),
        complete: sum(managerActivity, "complete"),
        overdue: sum(managerCurrentWork, "overdue"),
      };
    })
    .sort((left, right) => left.staff.localeCompare(right.staff));
};

export const buildSnapshotBacklogRows = (
  backlogRows,
  { staff = "all", region = "all" } = {},
) => {
  const groupedRows = new Map();
  backlogRows
    .filter(
      row =>
        (staff === "all" || String(row.staffId) === String(staff)) &&
        (region === "all" || row.province === region),
    )
    .forEach(row => {
      const existing = groupedRows.get(row.nextStep) || {
        id: row.nextStep,
        stage: row.nextStep,
        age0To7: 0,
        age8To14: 0,
        age15To30: 0,
        age31Plus: 0,
        ageUnknown: 0,
      };
      existing.age0To7 += Number(row.age0To7) || 0;
      existing.age8To14 += Number(row.age8To14) || 0;
      existing.age15To30 += Number(row.age15To30) || 0;
      existing.age31Plus += Number(row.age31Plus) || 0;
      existing.ageUnknown += Number(row.ageUnknown) || 0;
      groupedRows.set(row.nextStep, existing);
    });

  return Array.from(groupedRows.values())
    .map(row => ({
      ...row,
      total:
        row.age0To7 +
        row.age8To14 +
        row.age15To30 +
        row.age31Plus +
        row.ageUnknown,
    }))
    .sort((left, right) => {
      const leftIndex = BACKLOG_STEP_ORDER.indexOf(left.stage);
      const rightIndex = BACKLOG_STEP_ORDER.indexOf(right.stage);
      return (leftIndex < 0 ? 999 : leftIndex) - (rightIndex < 0 ? 999 : rightIndex);
    });
};

export const countSupportedParticipants = (snapshot, { staff = "all", region = "all" } = {}) => {
  const rows = (snapshot?.currentSupport || []).filter(row =>
    (staff === "all" || row.staffIds.includes(String(staff))) &&
    (region === "all" || row.province === region));
  return rows.some(row => row.unlinkedCases > 0) ? null : sum(rows, "participants");
};

export const buildSnapshotCaseWorkloadRows = (
  snapshot,
  { staff = "all", region = "all" },
) =>
  (snapshot.staffMembers || [])
    .filter(manager => staff === "all" || String(manager.value) === String(staff))
    .filter(manager => region === "all" || (manager.provinces || []).includes(region))
    .map(manager => {
      const managerActivity = (snapshot.caseWorkload || []).filter(
        row =>
          String(row.staffId) === String(manager.value) &&
          (region === "all" || row.province === region),
      );
      return {
        id: String(manager.value),
        staff: manager.label,
        region: (manager.provinces || []).join(", ") || "—",
        ...fundingForStaff(snapshot, manager.value, region),
        openServiceCases: sum(managerActivity, "openServiceCases"),
        draftPlans: sum(managerActivity, "draftPlans"),
        activePlans: sum(managerActivity, "activePlans"),
        plannedInterventions: sum(managerActivity, "plannedInterventions"),
        inProgressInterventions: sum(managerActivity, "inProgressInterventions"),
        overdueFollowups: sum(managerActivity, "overdueFollowups"),
      };
    })
    .sort((left, right) => left.staff.localeCompare(right.staff));

const DEFAULT_LAYOUT = [
  { id: "throughput", rowSpan: 5, columnSpan: 4 },
  { id: "applicationWorkload", rowSpan: 5, columnSpan: 2 },
  { id: "caseWorkload", rowSpan: 5, columnSpan: 2 },
  { id: "backlog", rowSpan: 5, columnSpan: 2 },
  { id: "regionalFunding", rowSpan: 5, columnSpan: 2 },
];

const WIDGETS = {
  applicationWorkload: {
    title: "Application workload",
    hint: "Applications received in the selected period, grouped by assigned staff.",
    description:
      "Shows applications submitted in the selected period that are now assigned to each assigned staff member. Assessed means awaiting review, a decision, or follow-through; Complete means closed or withdrawn; Overdue means open files with overdue work. EI, CRF and Total Funding cover scheduled funding in the selected period for all currently assigned cases, including older applications and closed cases. They use the same rules as Financial Reports and Regional Snapshots, and repeat the Case workload funding totals.",
  },
  backlog: { title: "Backlog", hint: "Open applications grouped by next step and how long they have waited.", description: "Each open application is counted once under its recorded status or review stage. Age starts when it entered that status or stage; applications not yet in assessment are aged from submission." },
  caseWorkload: {
    title: "Case workload",
    hint: "Current cases and participant support, grouped by assigned staff.",
    description:
      "Participants currently supported counts each person once with an active plan or ongoing intervention, even without funding. It reflects support now, regardless of the selected period. Open service cases have an unfinished action plan or intervention; Overdue follow-ups counts cases with overdue work. EI, CRF and Total Funding include scheduled funding for all currently assigned cases in the selected period, including closed cases. These repeat the Application workload funding totals; do not add the two tables together.",
  },
  throughput: { title: "Operations trends", hint: "Monthly funding and operational activity across the selected period.", description: "Choose a chart, then choose which items to show. Figures are shown by month. Funding uses scheduled payment dates, matching Financial Reports and Regional Snapshots. Applications starts with Received, counting every application by the month it arrived. Optional outcomes show how those same applications have progressed. Hiding items does not change report totals. The trend follows the overall total, including hidden items. Where there are many items, the five largest are selected initially." },
  regionalFunding: { title: "Funding by region", hint: "Scheduled client funding for the selected period, grouped by region.", description: "EI, CRF and Total Funding use the same scheduled-payment rules as Financial Reports and Regional Snapshots. The overall total follows your staff and region filters. These amounts also appear in the workload tables; do not add the widgets together." },
};

const toBoardItems = layout =>
  layout
    .filter(item => WIDGETS[item.id])
    .map(item => ({
      ...item,
      ...(item.id === "throughput" ? { definition: { minColumnSpan: 2 } } : {}),
      data: { title: WIDGETS[item.id].title, description: WIDGETS[item.id].description },
    }));

const exportLayout = items =>
  items.map(({ id, rowSpan, columnSpan, columnOffset }) => ({
    id,
    rowSpan,
    columnSpan,
    columnOffset,
  }));

const loadLayout = () => {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (!Array.isArray(parsed)) return null;
    const filtered = parsed.filter(item => item?.id && WIDGETS[item.id]);
    return filtered.length ? filtered : null;
  } catch {
    return null;
  }
};

const persistLayout = layout => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // The dashboard remains usable when browser storage is unavailable.
  }
};

const areLayoutsEqual = (left = [], right = []) =>
  JSON.stringify(exportLayout(left)) === JSON.stringify(exportLayout(right));

const boardI18nStrings = {
  liveAnnouncementDndStarted: operationType => (operationType === "resize" ? "Resizing" : "Dragging"),
  liveAnnouncementDndItemReordered: operation =>
    `Item moved to ${operation.direction === "horizontal" ? `column ${operation.placement.x + 1}` : `row ${operation.placement.y + 1}`}.`,
  liveAnnouncementDndItemResized: operation =>
    `Item resized to ${operation.direction === "horizontal" ? `${operation.placement.width} columns` : `${operation.placement.height} rows`}.`,
  liveAnnouncementDndItemInserted: operation =>
    `Item inserted into column ${operation.placement.x + 1}, row ${operation.placement.y + 1}.`,
  liveAnnouncementDndCommitted: operationType => `${operationType} committed.`,
  liveAnnouncementDndDiscarded: operationType => `${operationType} cancelled.`,
  liveAnnouncementItemRemoved: operation => `Removed ${operation.item.data.title}.`,
  navigationAriaLabel: "Operations Report navigation",
  navigationAriaDescription: "Use arrow keys to move between dashboard sections.",
  navigationItemAriaLabel: item => item?.data?.title || "Empty dashboard position",
};

const boardItemI18nStrings = {
  dragHandleAriaLabel: "Drag handle",
  dragHandleAriaDescription:
    "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to cancel.",
  resizeHandleAriaLabel: "Resize handle",
  resizeHandleAriaDescription:
    "Use Space or Enter to activate resize, arrow keys to resize, Space or Enter to submit, or Escape to cancel.",
};

const EmptyState = () => (
  <Box padding="m" textAlign="center" color="text-status-inactive">
    No records match these filters.
  </Box>
);

const Widget = ({ item, actions, children }) => (
  <BoardItem
    header={
      <Header variant="h2" description={<span style={{ display: "block", lineHeight: 1.4, minHeight: "2.8em" }}>{WIDGETS[item.id].hint}</span>} info={
        <Popover triggerType="click" size="large" header={item.data.title} content={item.data.description}>
          <Link variant="info" ariaLabel={`About ${item.data.title}`}>Info</Link>
        </Popover>
      }>
        {item.data.title}
      </Header>
    }
    settings={
      actions?.removeItem ? (
        <ButtonDropdown
          items={[{ id: "remove", text: "Remove section" }]}
          ariaLabel={`${item.data.title} settings`}
          variant="icon"
          onItemClick={({ detail }) => detail.id === "remove" && actions.removeItem()}
        />
      ) : null
    }
    i18nStrings={boardItemI18nStrings}
  >
    {children}
  </BoardItem>
);

const OperationsAndTimelinessDashboard = ({
  updateBreadcrumbs,
  setAvailableItems,
  setSplitPanelOpen,
}) => {
  const [fiscalYear, setFiscalYear] = useState(FISCAL_YEAR_OPTIONS[0]);
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);
  const [staff, setStaff] = useState(STAFF_OPTIONS[0]);
  const [staffOptions, setStaffOptions] = useState(STAFF_OPTIONS);
  const [region, setRegion] = useState(REGION_OPTIONS[0]);
  const [layout, setLayout] = useState(() => loadLayout() || [...DEFAULT_LAYOUT]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const boardItems = useMemo(() => toBoardItems(layout), [layout]);
  const availableItems = useMemo(
    () =>
      Object.entries(WIDGETS)
        .filter(([id]) => !boardItems.some(item => item.id === id))
        .map(([id, widget]) => ({ id, data: { title: widget.title, description: widget.description } })),
    [boardItems],
  );
  const paletteSignatureRef = useRef(JSON.stringify(availableItems.map(item => item.id)));
  const layoutSignatureRef = useRef(JSON.stringify(exportLayout(boardItems)));

  const applicationWorkloadRows = useMemo(
    () =>
      reportData
        ? buildSnapshotApplicationWorkloadRows(reportData, {
            fiscalYear: fiscalYear.value,
            period: period.value,
            staff: staff.value,
            region: region.value,
          })
        : [],
    [staff.value, reportData, fiscalYear.value, period.value, region.value],
  );
  const backlogRows = useMemo(
    () =>
      reportData
        ? buildSnapshotBacklogRows(reportData.currentBacklog, {
            staff: staff.value,
            region: region.value,
          })
        : [],
    [staff.value, reportData, region.value],
  );
  const regionalFundingRows = useMemo(
    () => reportData ? buildRegionalFundingRows(reportData, { staff: staff.value, region: region.value }) : [],
    [reportData, staff.value, region.value],
  );
  const caseWorkloadRows = useMemo(
    () =>
      reportData
        ? buildSnapshotCaseWorkloadRows(reportData, {
            staff: staff.value,
            region: region.value,
          })
        : [],
    [staff.value, reportData, region.value],
  );
  const supportedParticipants = reportData ? countSupportedParticipants(reportData, { staff: staff.value, region: region.value }) : null;
  useEffect(() => {
    updateBreadcrumbs?.([
      { text: "Home", href: "/" },
      { text: "Reporting" },
      { text: "Operations Report", href: "/reporting/operations-and-timeliness" },
    ]);
  }, [updateBreadcrumbs]);

  useEffect(() => {
    const controller = new AbortController();
    setReportData(null);
    setLoading(true);
    setLoadError(false);
    const query = new URLSearchParams({
      fiscalYearStart: String(fiscalYear.startYear),
      periodType: period.value === "full" ? "year" : period.value.startsWith("q") ? "quarter" : "month",
      periodKey: period.value.startsWith("m") ? MONTHS[period.monthIndexes[0]].label.toLowerCase() : period.value,
    });
    apiFetch(`/api/reporting/operations-report?${query}`, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error("report_fetch_failed");
        return response.json();
      })
      .then(report => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(report?.trends?.rows) || !["staffMembers", "applicationActivity", "workloadActivity", "currentWorkload", "currentBacklog", "caseWorkload", "currentSupport", "fundingActivity"].every(key => Array.isArray(report?.[key]))) {
          throw new Error("invalid_report");
        }
        const options = [...STAFF_OPTIONS, ...report.staffMembers.map(item => ({
          label: item.label, value: String(item.value), description: item.role || undefined,
        }))];
        setStaffOptions(options);
        setStaff(current => options.find(item => item.value === current.value) || options[0]);
        setReportData(report);
      })
      .catch(() => { if (!controller.signal.aborted) setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [fiscalYear.startYear, period]);

  useEffect(() => {
    const signature = JSON.stringify(availableItems.map(item => item.id));
    if (signature !== paletteSignatureRef.current) {
      paletteSignatureRef.current = signature;
      setAvailableItems?.(availableItems);
    }
    const layoutSignature = JSON.stringify(exportLayout(boardItems));
    if (layoutSignature !== layoutSignatureRef.current) {
      layoutSignatureRef.current = layoutSignature;
      persistLayout(exportLayout(boardItems));
    }
  }, [availableItems, boardItems, setAvailableItems]);

  const openPalette = useCallback(() => {
    setAvailableItems?.(availableItems);
    setSplitPanelOpen?.(true);
  }, [availableItems, setAvailableItems, setSplitPanelOpen]);

  const resetLayout = useCallback(() => {
    setLayout(current => (areLayoutsEqual(current, DEFAULT_LAYOUT) ? current : [...DEFAULT_LAYOUT]));
    const defaultAvailableItems = [];
    paletteSignatureRef.current = JSON.stringify(defaultAvailableItems);
    layoutSignatureRef.current = JSON.stringify(DEFAULT_LAYOUT);
    setAvailableItems?.(defaultAvailableItems);
    persistLayout(DEFAULT_LAYOUT);
  }, [setAvailableItems]);

  useEffect(() => {
    const openHandler = () => openPalette();
    const resetHandler = () => resetLayout();
    window.addEventListener("operationsTimeliness:openPalette", openHandler);
    window.addEventListener("operationsTimeliness:resetLayout", resetHandler);
    return () => {
      window.removeEventListener("operationsTimeliness:openPalette", openHandler);
      window.removeEventListener("operationsTimeliness:resetLayout", resetHandler);
    };
  }, [openPalette, resetLayout]);

  const onItemsChange = useCallback(({ detail }) => {
    if (!Array.isArray(detail?.items)) return;
    const nextLayout = exportLayout(detail.items);
    setLayout(current => (areLayoutsEqual(current, nextLayout) ? current : nextLayout));
  }, []);

  const renderItem = useCallback(
    (item, actions) => {
      switch (item.id) {
        case "applicationWorkload":
          return (
            <Widget item={item} actions={actions}>
              <OperationsReportTable
                id={item.id}
                title={item.data.title}
                loading={loading}
                items={applicationWorkloadRows}
                columnDefinitions={[
                  { id: "staff", header: "Assigned staff", cell: row => row.staff },
                  { id: "region", header: "Provinces/territories", cell: row => row.region },
                  { id: "assigned", header: "Assigned", cell: row => row.assigned },
                  { id: "notStarted", header: "Not started", cell: row => row.notStarted },
                  { id: "inProgress", header: "In progress", cell: row => row.inProgress },
                  { id: "assessed", header: "Assessed", cell: row => row.assessed },
                  { id: "complete", header: "Complete", cell: row => row.complete },
                  { id: "overdue", header: "Overdue", cell: row => row.overdue },
                  ...FUNDING_COLUMNS,
                ]}
                empty={<EmptyState />}
              />
            </Widget>
          );
        case "backlog":
          return (
            <Widget item={item} actions={actions}>
              <OperationsReportTable
                id={item.id}
                title={item.data.title}
                loading={loading}
                items={backlogRows}
                columnDefinitions={[
                  { id: "stage", header: "Next step", cell: row => row.stage },
                  { id: "age0To7", header: "0–7 days", cell: row => row.age0To7 },
                  { id: "age8To14", header: "8–14 days", cell: row => row.age8To14 },
                  { id: "age15To30", header: "15–30 days", cell: row => row.age15To30 },
                  { id: "age31Plus", header: "31+ days", cell: row => row.age31Plus },
                  { id: "ageUnknown", header: "Date unavailable", cell: row => row.ageUnknown },
                  { id: "total", header: "Total", cell: row => row.total },
                ]}
                empty={<EmptyState />}
              />
            </Widget>
          );
        case "caseWorkload":
          return (
            <Widget item={item} actions={actions}>
              {!loading && reportData && supportedParticipants === null && <Box color="text-status-warning">Some active files need a participant link before this count is available.</Box>}
              <OperationsReportTable
                id={item.id}
                title={item.data.title}
                loading={loading}
                summary={<strong>{supportedParticipants === null ? "—" : supportedParticipants} participants currently supported</strong>}
                items={caseWorkloadRows}
                columnDefinitions={[
                  { id: "staff", header: "Assigned staff", cell: row => row.staff },
                  { id: "region", header: "Provinces/territories", cell: row => row.region },
                  {
                    id: "openServiceCases",
                    header: "Open service cases",
                    cell: row => row.openServiceCases,
                  },
                  { id: "draftPlans", header: "Plans in draft", cell: row => row.draftPlans },
                  { id: "activePlans", header: "Active plans", cell: row => row.activePlans },
                  {
                    id: "plannedInterventions",
                    header: "Interventions planned",
                    cell: row => row.plannedInterventions,
                  },
                  {
                    id: "inProgressInterventions",
                    header: "Interventions underway",
                    cell: row => row.inProgressInterventions,
                  },
                  {
                    id: "overdueFollowups",
                    header: "Overdue follow-ups",
                    cell: row => row.overdueFollowups,
                  },
                  ...FUNDING_COLUMNS,
                ]}
                empty={<EmptyState />}
              />
            </Widget>
          );
        case "throughput":
          return <Widget item={item} actions={actions}>
            <OperationsTrends report={reportData} fiscalYearStart={fiscalYear.startYear}
              monthIndexes={period.monthIndexes} staff={staff.value} region={region.value} loading={loading} />
          </Widget>;
        case "regionalFunding":
          return (
            <Widget item={item} actions={actions}>
              <OperationsReportTable
                id={item.id}
                title={item.data.title}
                loading={loading}
                items={regionalFundingRows}
                columnDefinitions={[
                  { id: "region", header: "Region", minWidth: 130, cell: row => row.isTotal ? <strong>{row.region}</strong> : row.region },
                  ...FUNDING_COLUMNS.map(column => ({
                    ...column,
                    minWidth: 170,
                    cell: row => row.isTotal ? <strong>{column.cell(row)}</strong> : column.cell(row),
                  })),
                ]}
                empty={<EmptyState />}
              />
            </Widget>
          );
        default:
          return null;
      }
    },
    [
      loading,
      backlogRows,
      caseWorkloadRows,
      supportedParticipants,
      regionalFundingRows,
      reportData, fiscalYear.startYear, period.monthIndexes, staff.value, region.value,
      applicationWorkloadRows,
    ],
  );

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Report controls</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={4} variant="text-grid">
            <FormField label="Fiscal year">
              <Select
                selectedOption={fiscalYear}
                options={FISCAL_YEAR_OPTIONS}
                onChange={({ detail }) => setFiscalYear(detail.selectedOption)}
              />
            </FormField>
            <FormField label="Period">
              <Select
                selectedOption={period}
                options={PERIOD_OPTIONS}
                onChange={({ detail }) => setPeriod(detail.selectedOption)}
              />
            </FormField>
            <FormField label="Assigned staff">
              <Select
                selectedOption={staff}
                options={staffOptions}
                onChange={({ detail }) => setStaff(detail.selectedOption)}
              />
            </FormField>
            <FormField label="Participant region">
              <Select
                selectedOption={region}
                options={REGION_OPTIONS}
                onChange={({ detail }) => setRegion(detail.selectedOption)}
              />
            </FormField>
          </ColumnLayout>
          <Box color="text-body-secondary" fontSize="body-s">
            Fiscal years run from April to March.
          </Box>
        </SpaceBetween>
      </Container>

      {!loading && (region.value === 'all' || region.value === 'UNASSIGNED') && (reportData?.fundingActivity || []).some(row => row.province === 'UNASSIGNED' && row.totalFunding > 0 && (staff.value === 'all' || String(row.staffId) === String(staff.value))) && <Alert type="warning">Funding under Unassigned pot is included in the overall total and needs a budget assignment. Review it in Financial Reports.</Alert>}
      {loadError && <Alert type="error">The report could not be loaded. Refresh the page to try again.</Alert>}
      {!loadError && <Board
        boardId="operations-and-timeliness-dashboard"
        items={boardItems}
        renderItem={renderItem}
        onItemsChange={onItemsChange}
        i18nStrings={boardI18nStrings}
        empty={
          <Box padding="m" textAlign="center" color="text-status-inactive">
            Add sections from the page header to rebuild the dashboard.
          </Box>
        }
      />}
    </SpaceBetween>
  );
};

export default OperationsAndTimelinessDashboard;
